/**
 * ScopeSelector
 *
 * Universal "what am I working against" control.
 * Displays the entitled jurisdiction tree + platform/org themes + optional date range.
 * A saved selection = a workspace scope expression.
 */

import React, { useState, useEffect } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Tag,
  MapPin,
  Calendar,
  Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '@/lib/apiFetch';
import type { JurisdictionTreeNode, Theme, OrgTheme } from '@/types/database';

export interface ScopeSelection {
  jurisdictionId?: string | null;
  jurisdictionName?: string;
  themeId?: string | null;
  orgThemeId?: string | null;
  themeName?: string;
  dateFrom?: string | null;
  dateTo?: string | null;
}

interface Props {
  value: ScopeSelection;
  onChange: (selection: ScopeSelection) => void;
  /** If true, shows the date range pickers */
  showDateRange?: boolean;
  className?: string;
}

export const ScopeSelector: React.FC<Props> = ({
  value,
  onChange,
  showDateRange = false,
  className = '',
}) => {
  const [tree, setTree] = useState<JurisdictionTreeNode[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [orgThemes, setOrgThemes] = useState<OrgTheme[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [treeRes, themesRes, orgThemesRes] = await Promise.all([
          apiFetch('/api/jurisdictions/tree'),
          apiFetch('/api/themes'),
          apiFetch('/api/org-themes'),
        ]);
        if (treeRes.ok) {
          const d = await treeRes.json();
          setTree(d.data ?? []);
        }
        if (themesRes.ok) {
          const d = await themesRes.json();
          setThemes(d.data ?? []);
        }
        if (orgThemesRes.ok) {
          const d = await orgThemesRes.json();
          setOrgThemes(d.data ?? []);
        }
      } catch {
        // non-fatal
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectJurisdiction = (node: JurisdictionTreeNode) => {
    if (node.frozen) return; // lapsed — cannot select for new work
    onChange({
      ...value,
      jurisdictionId: node.id,
      jurisdictionName: node.name,
    });
  };

  const renderNode = (
    node: JurisdictionTreeNode,
    depth = 0
  ): React.ReactNode => {
    const isSelected = value.jurisdictionId === node.id;
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expanded.has(node.id);

    return (
      <div key={node.id}>
        <div
          className={`flex cursor-pointer items-center gap-1 rounded px-2 py-1 text-sm transition-colors
            ${isSelected ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'}
            ${node.frozen ? 'cursor-not-allowed opacity-60' : ''}
          `}
          style={{ paddingLeft: `${8 + depth * 16}px` }}
          onClick={() => selectJurisdiction(node)}
        >
          {hasChildren ? (
            <button
              className="mr-1 shrink-0 text-gray-400"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(node.id);
              }}
            >
              {isExpanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </button>
          ) : (
            <span className="mr-1 w-4 shrink-0" />
          )}
          <MapPin className="mr-1 h-3 w-3 shrink-0 text-gray-400" />
          <span className="truncate">{node.name}</span>
          {node.frozen && (
            <Lock className="ml-1 h-3 w-3 shrink-0 text-amber-500" />
          )}
        </div>
        {isExpanded &&
          node.children?.map((child) => renderNode(child, depth + 1))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`text-sm text-gray-500 ${className}`}>
        Loading scope...
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Jurisdiction picker */}
      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
          Jurisdiction
        </h4>
        {tree.length === 0 ? (
          <p className="text-sm text-gray-400">
            No entitled jurisdictions.{' '}
            <a href="/coverage" className="text-blue-600 underline">
              View coverage
            </a>
          </p>
        ) : (
          <div className="max-h-48 overflow-y-auto rounded border border-gray-200 bg-white">
            {tree.map((node) => renderNode(node))}
          </div>
        )}
        {value.jurisdictionId && (
          <div className="mt-1 flex items-center gap-1">
            <Badge variant="secondary" className="text-xs">
              {value.jurisdictionName}
            </Badge>
            <button
              className="text-xs text-gray-400 hover:text-gray-600"
              onClick={() =>
                onChange({
                  ...value,
                  jurisdictionId: null,
                  jurisdictionName: undefined,
                })
              }
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* Theme picker */}
      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
          Theme
        </h4>
        <div className="flex flex-wrap gap-1">
          {themes.map((t) => (
            <button
              key={t.id}
              onClick={() =>
                onChange({
                  ...value,
                  themeId: t.id,
                  orgThemeId: null,
                  themeName: t.name,
                })
              }
              className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors
                ${
                  value.themeId === t.id
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-blue-300'
                }`}
            >
              <Tag className="h-3 w-3" />
              {t.name}
            </button>
          ))}
          {orgThemes.map((t) => (
            <button
              key={t.id}
              onClick={() =>
                onChange({
                  ...value,
                  orgThemeId: t.id,
                  themeId: null,
                  themeName: t.name,
                })
              }
              className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors
                ${
                  value.orgThemeId === t.id
                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-purple-300'
                }`}
            >
              <Tag className="h-3 w-3" />
              {t.name}
              <span className="text-[10px] text-gray-400">custom</span>
            </button>
          ))}
        </div>
        {(value.themeId || value.orgThemeId) && (
          <div className="mt-1 flex items-center gap-1">
            <Badge variant="secondary" className="text-xs">
              {value.themeName}
            </Badge>
            <button
              className="text-xs text-gray-400 hover:text-gray-600"
              onClick={() =>
                onChange({
                  ...value,
                  themeId: null,
                  orgThemeId: null,
                  themeName: undefined,
                })
              }
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* Optional date range */}
      {showDateRange && (
        <div>
          <h4 className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
            <Calendar className="h-3 w-3" />
            Date Range
          </h4>
          <div className="flex items-center gap-2">
            <input
              type="date"
              className="rounded border border-gray-200 px-2 py-1 text-xs"
              value={value.dateFrom ?? ''}
              onChange={(e) =>
                onChange({ ...value, dateFrom: e.target.value || null })
              }
            />
            <span className="text-xs text-gray-400">to</span>
            <input
              type="date"
              className="rounded border border-gray-200 px-2 py-1 text-xs"
              value={value.dateTo ?? ''}
              onChange={(e) =>
                onChange({ ...value, dateTo: e.target.value || null })
              }
            />
            {(value.dateFrom || value.dateTo) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-1 text-xs"
                onClick={() =>
                  onChange({ ...value, dateFrom: null, dateTo: null })
                }
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
